import { DataTable, DataTableSortEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { useState, useRef, useEffect } from "react";
import orderServices from '../../services/orders/orderservice';
import PackingNewOrderReview from "../Packing-Orders/packing-order-review";
import { DataTableRowToggleEvent } from "primereact/datatable";

interface Order {
  orderId: number;
  orderDate: string;
  binNo: number | null;
  primaryContactNbr: string;
  subDistrictName: string;
  expanded?: boolean;
  details?: any;
}

interface BinDetailsProps {
  data: {
    bins: any[];
    totalRecordCount: number;
  } | null;
  onPageChange?: (event: any) => void;
  pageNumber?: number;
  pageSize?: number;
  onBinUpdate?: () => void;
}

const BinDetails: React.FC<BinDetailsProps> = ({
  data,
  onPageChange,
  pageNumber = 1,
  pageSize = 10,
  onBinUpdate
}) => {
  const toast = useRef<Toast>(null);
  const [localData, setLocalData] = useState<Order[]>([]);
  const [sortField, setSortField] = useState<string>('orderId');
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(1);
  const [loadingDetails, setLoadingDetails] = useState<Record<number, boolean>>({});
  const [refreshDetails, setRefreshDetails] = useState<Record<number, boolean>>({});
  const [editingBin, setEditingBin] = useState<{ orderId: number | null, binNo: string | null }>({ orderId: null, binNo: null });

  useEffect(() => {
    if (data?.bins) {
      const transformed = data.bins.map(bin => ({
        orderId: bin.orderID,
        orderDate: new Intl.DateTimeFormat('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        }).format(new Date(bin.orderDate)),
        binNo: bin.binNo || null,
        primaryContactNbr: bin.primaryContactNbr,
        subDistrictName: bin.subDistrictName,
        expanded: false,
        details: null
      }));

      const sortedData = [...transformed].sort((a, b) => {
        const valueA = a[sortField as keyof Order];
        const valueB = b[sortField as keyof Order];

        if (valueA == null) return 1;
        if (valueB == null) return -1;
        if (valueA === valueB) return 0;

        return sortOrder === 1
          ? valueA.toString().localeCompare(valueB.toString())
          : valueB.toString().localeCompare(valueA.toString());
      });

      setLocalData(sortedData);
    } else {
      setLocalData([]);
    }
  }, [data, sortField, sortOrder]);

  const showMessage = (severity: 'success' | 'info' | 'warn' | 'error', summary: string, detail: string) => {
    toast.current?.show({ severity, summary, detail, life: 3000 });
  };

  const handleBinNoChange = (orderId: number, value: string) => {
    if (/^\d*$/.test(value)) {
      if (value === "0") {
        setEditingBin({ orderId, binNo: "" });
        showMessage('error', 'Error', 'Bin number must be greater than or equal to 1');
      } else {
        setEditingBin({ orderId, binNo: value });
      }
    }
  };

  const saveBinNo = async (orderId: number) => {
    if (editingBin.orderId === orderId && editingBin.binNo !== null) {
      const binNo = editingBin.binNo.trim();

      if (binNo === '') {
        showMessage('error', 'Error', 'Please enter a valid bin number');
        return;
      }

      if (!/^\d+$/.test(binNo)) {
        showMessage('error', 'Error', 'Please enter a valid number');
        return;
      }

      const binNum = Number(binNo);
      if (binNum < 1) {
        showMessage('error', 'Error', 'Bin number must be greater than or equal to 1');
        return;
      }

      const currentOrder = localData.find(order => order.orderId === orderId);
      const currentBinNo = currentOrder?.binNo?.toString() ?? '';

      if (currentBinNo === binNo) {
        setEditingBin({ orderId: null, binNo: null });
        return;
      }

      try {
        await orderServices.saveOrderPacking({
          orderID: orderId,
          packingNo: binNum
        });

        setLocalData(prevData =>
          prevData.map(item =>
            item.orderId === orderId
              ? { ...item, binNo: binNum }
              : item
          )
        );

        setRefreshDetails(prev => ({ ...prev, [orderId]: true }));
        setEditingBin({ orderId: null, binNo: null });

        if (onBinUpdate) {
          onBinUpdate();
        }

        showMessage('success', 'Success', 'Bin number updated successfully');
      } catch (error) {
        console.error('Error updating bin number:', error);
        showMessage('error', 'Error', 'Failed to update bin number');
      }
    }
  };

  const toggleRowExpand = async (orderId: number) => {
    setLocalData(prevData => {
      const newData = [...prevData];
      const rowIndex = newData.findIndex(row => row.orderId === orderId);

      if (rowIndex !== -1) {
        const isExpanding = !newData[rowIndex].expanded;
        newData[rowIndex].expanded = isExpanding;

        if (isExpanding && (!newData[rowIndex].details || refreshDetails[orderId])) {
          setLoadingDetails(prev => ({ ...prev, [orderId]: true }));
          loadOrderDetails(orderId, rowIndex);
          setRefreshDetails(prev => ({ ...prev, [orderId]: false }));
        }
      }
      return newData;
    });
  };

  const loadOrderDetails = async (orderId: number, rowIndex: number) => {
    try {
      const details = await orderServices.GetPackingOrderByOrderId(orderId);
      setLocalData(prevData => {
        const newData = [...prevData];
        if (rowIndex !== -1 && newData[rowIndex].orderId === orderId) {
          newData[rowIndex].details = details;
        }
        return newData;
      });
    } catch (error) {
      console.error('Error fetching order details:', error);
      showMessage('error', 'Error', 'Failed to load order details');
    } finally {
      setLoadingDetails(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const handleSort = (e: DataTableSortEvent) => {
    const newSortField = e.sortField;
    let newSortOrder = e.sortOrder as 1 | -1 | null;
    if (newSortField === sortField) {
      if (sortOrder === 1) {
        newSortOrder = -1;
      } else if (sortOrder === -1) {
        newSortOrder = null;
      } else {
        newSortOrder = 1;
      }
    } else {
      newSortOrder = 1;
    }
    setSortField(newSortField);
    setSortOrder(newSortOrder);
  };

  const rowExpansionTemplate = (rowData: Order) => {
    return (
      <div className="p-4" style={{ backgroundColor: '#f8f9fa' }}>
        <PackingNewOrderReview
          key={`order-${rowData.orderId}-${rowData.binNo}`}
          OrderData={rowData.details}
          PrintEnabled={false}
          TriggerPrint={() => { }}
        />
      </div>
    );
  };

  const binNumberCell = (rowData: Order) => {
    const isEditing = editingBin.orderId === rowData.orderId;

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter'].includes(e.key)) {
        return;
      }

      if (!/^\d$/.test(e.key)) {
        e.preventDefault();
      }
    };

    return (
      <div>
        <InputText
          type="text"
          value={isEditing ? editingBin.binNo ?? '' : rowData.binNo?.toString() ?? ''}
          onChange={(e) => handleBinNoChange(rowData.orderId, e.target.value)}
          onKeyDown={(e) => {
            handleKeyPress(e);
            if (e.key === 'Enter') {
              saveBinNo(rowData.orderId);
            }
          }}
          onFocus={() => {
            setEditingBin({ orderId: rowData.orderId, binNo: rowData.binNo?.toString() || '' });
          }}
          onBlur={() => saveBinNo(rowData.orderId)}
          style={{ width: '100%' }}
        />
      </div>
    );
  };

  return (
    <div className="card p-fluid">
      <Toast ref={toast} />
      {localData.length > 0 ? (
        <DataTable
          value={localData}
          paginator
          stripedRows
          showGridlines
          rows={pageSize}
          first={(pageNumber - 1) * pageSize}
          onPage={onPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
          totalRecords={data?.totalRecordCount || 0}
          tableStyle={{ minWidth: '50rem' }}
          onSort={handleSort}
          sortMode="single"
          sortField={sortField}
          sortOrder={sortOrder}
          removableSort
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate={`{first} to {last} of ${data?.totalRecordCount}`}
          emptyMessage="No orders found"
          lazy
          expandedRows={localData.filter(row => row.expanded)}
          onRowToggle={(e: DataTableRowToggleEvent) => {
            const rowData = e.data as unknown as Order;
            toggleRowExpand(rowData.orderId);
          }}
          rowExpansionTemplate={rowExpansionTemplate}
        >
          <Column
            field="orderId"
            header="Order #"
            sortable
            body={(rowData: Order) => (
              <span
                style={{ color: "darkcyan", fontWeight: "bolder", cursor: "pointer" }}
                onClick={() => toggleRowExpand(rowData.orderId)}
              >
                {rowData.orderId}
              </span>
            )}
          />
          <Column
            field="orderDate"
            header="Order Date"
            sortable
          />
          <Column
            field="binNo"
            header="Bin #"
            sortable
            body={binNumberCell}
          />
          <Column
            field="primaryContactNbr"
            header="Mobile #"
            sortable
          />
          <Column
            field="subDistrictName"
            header="City Name"
            sortable
          />
        </DataTable>
      ) : (
        <div className="p-4 text-center">No orders found</div>
      )}
    </div>
  );
};

export default BinDetails;